import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { useCamp } from "@/context/SettingsContext";
import { mapSrc } from "@/components/dashboard/CampsiteMap";
import { fmtDate } from "@/lib/constants";
import PitchMarker from "@/components/dashboard/PitchMarker";
import { SIZE_PCT } from "@/components/dashboard/CampsiteMap";
import { STATUS, FLAGS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import RegistrationForm from "@/components/print/RegistrationForm";
import PrintLogo from "@/components/print/PrintLogo";
import DemoStamp from "@/components/DemoStamp";
import { usePrintT } from "@/lib/printI18n";


const md = (iso) => (iso ? iso.slice(8, 10) + "/" + iso.slice(5, 7) : "");

// "Zonder verhuis": per zone gegroepeerd (kleur + code + naam), de plaatsen die de
// volledige periode vrij zijn.
function DirectByZone({ title, items, zones, testid }) {
  const byZone = {};
  items.forEach((o) => { (byZone[o.zone] ??= []).push(o.start_label); });
  const list = (zones || []).filter((z) => byZone[z.code]?.length);
  return (
    <div className="border-b border-slate-100 pb-1.5" data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: "#16a34a" }}>{title} · {items.length}</p>
      {list.length === 0 ? (
        <p className="text-[11px] text-slate-400">—</p>
      ) : (
        <div className="space-y-1">
          {list.map((z) => (
            <div key={z.code} className="flex items-start gap-2 text-[11px] leading-snug" data-testid={`print-free-zone-${z.code}`}>
              <span className="mt-[1px] h-4 w-4 shrink-0 rounded grid place-items-center text-[8px] font-bold text-white" style={{ background: z.color }}>{z.code}</span>
              <span className="w-24 shrink-0 font-semibold">{z.name}</span>
              <span className="w-10 shrink-0 font-bold tabular-nums" style={{ color: z.color }}>{byZone[z.code].length}</span>
              <span className="flex-1 text-slate-700">{byZone[z.code].join(", ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Vrije-plaatsen-groep op de print: zonder/1/2 verhuizingen. Bij ketens tonen we
// per plaats de einddatum (de verhuisdag, en bij de laatste plaats de vertrekdatum).
function FreeGroup({ title, color, items, testid, chain = false, until }) {
  const endOf = (ch, i) => (i < ch.length - 1 ? ch[i + 1].from : ch[i].to);
  return (
    <div className="border-b border-slate-100 pb-1.5" data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color }}>{title} · {items.length}</p>
      {items.length === 0 ? (
        <p className="text-[11px] text-slate-400">—</p>
      ) : !chain ? (
        <p className="text-[11px] text-slate-700 leading-snug">{items.map((o) => o.start_label).join(", ")}</p>
      ) : (
        <div className="flex flex-col gap-0.5">
          {items.map((o) => (
            <div key={o.start_label} className="text-[11px] text-slate-700 leading-snug">
              {o.chain.map((c, i) => (
                <span key={i}>
                  {i > 0 && <span className="text-slate-400"> → </span>}
                  <span className="font-semibold">{c.pitch_label}</span>
                  <span className="text-slate-500"> {until} {md(endOf(o.chain, i))}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



export default function PrintPage() {
  const { t: tUi, lang: uiLang } = useLang();
  const { camp } = useCamp();
  const MAP_SRC = mapSrc(camp);
  const [params] = useSearchParams();
  const from = params.get("from");
  const to = params.get("to");
  const lang = params.get("lang") || uiLang;
  const resId = params.get("res");
  const withMap = !resId || params.get("map") === "1";
  const [resv, setResv] = useState(null);
  const tDoc = usePrintT(lang);
  const t = (k) => tDoc(k) ?? tUi(k);
  const [pitches, setPitches] = useState([]);
  const [parking, setParking] = useState(null);
  const [avail, setAvail] = useState(null);
  const [zones, setZones] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      const jobs = {};
      if (resId) jobs.res = api.get(`/reservations/${resId}`);
      if (withMap) { jobs.pitches = api.get("/pitches"); jobs.stats = api.get("/dashboard/stats"); }
      if (from && to) { jobs.avail = api.get(`/availability-plan?from=${from}&to=${to}`); jobs.zones = api.get("/zones"); }
      const res = await Promise.all(Object.values(jobs));
      const out = {};
      Object.keys(jobs).forEach((k, i) => { out[k] = res[i].data; });
      if (out.res) setResv(out.res);
      if (out.pitches) setPitches(out.pitches.filter((x) => x.x != null));
      if (out.stats) setParking(out.stats.parking);
      if (out.avail) setAvail(out.avail);
      if (out.zones) setZones(out.zones);
      setReady(true);
    })();
  }, [from, to, resId, withMap]);

  useEffect(() => {
    if (!ready) return;
    const img = new Image();
    img.src = MAP_SRC;
    img.onload = () => setTimeout(() => window.print(), 400);
  }, [ready, MAP_SRC]);

  return (
    <div className="print-page bg-white text-slate-900 mx-auto max-w-[1000px] p-6 print:p-0" data-testid="print-page">
      <div className={`flex items-start justify-between gap-4 ${resId ? "print:hidden" : "mb-3"}`}>
        {resId ? <span /> : (
          <div className="flex items-center gap-3">
            <PrintLogo camp={camp} testid="print-map-logo" />
            <h1 className="font-display font-extrabold text-xl leading-tight" data-testid="print-period">{t("free_title")}: {fmtDate(from)} – {fmtDate(to)}</h1>
          </div>
        )}
        <Button size="sm" onClick={() => window.print()} className="print:hidden" data-testid="print-now"><Printer className="h-4 w-4 mr-1" />{tUi("print")}</Button>
      </div>

      {withMap && (
      <div className="relative w-full border border-slate-300 rounded" style={{ aspectRatio: "2000 / 1413" }} data-testid="print-map">
        <img src={MAP_SRC} alt="Plattegrond" className="absolute inset-0 h-full w-full object-contain" />
        {pitches
          .filter((p) => p.x != null && ((p.status !== "vrij" && p.status !== "buiten_dienst") || p.label === resv?.pitch_label))
          .map((p) => (
            <PitchMarker key={p.id} p={p} x={p.x} y={p.y} lang={uiLang}
              px={p.label === resv?.pitch_label ? 30 : 950 * (SIZE_PCT[p.size] || SIZE_PCT[1])} />
          ))}
        {parking && (
          <span className="absolute rounded bg-slate-900 text-white px-1 text-[8px] font-bold" style={{ left: `${parking.x}%`, top: `${parking.y}%`, transform: "translate(-50%,-50%)" }}>P {parking.occupied}/{parking.capacity}</span>
        )}
      </div>
      )}

      {withMap && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[9px] text-slate-700 border border-slate-300 rounded px-3 py-1.5" data-testid="print-legend">
          <span className="font-semibold uppercase tracking-wider">{t("legend")}</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS.bezet.color }} />{t("st_occupied")}</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS.gereserveerd.color }} />{t("st_reserved")}</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border-2" style={{ borderColor: FLAGS.rood_lintje.color }} />{t("st_ribbon")}</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: FLAGS.controle_nodig.color }} />{t("st_control")}</span>
        </div>
      )}

      {avail && (
        <div className="mt-4" data-testid="print-availability">
          <div className="flex items-center justify-end mb-2">
            <span className="text-sm font-semibold" data-testid="print-total-free">{t("total")}: {avail.counts.direct + avail.counts.one_move + avail.counts.two_moves}</span>
          </div>
          <div className="space-y-2">
            <DirectByZone title={t("free_direct")} items={avail.direct} zones={zones} testid="print-free-direct" />
            <FreeGroup title={t("free_one")} color="#d97706" items={avail.one_move} testid="print-free-one" chain until={t("until")} />
            <FreeGroup title={t("free_two")} color="#e11d48" items={avail.two_moves} testid="print-free-two" chain until={t("until")} />
          </div>
        </div>
      )}

      <DemoStamp />
      {resId && resv && (
        <RegistrationForm lang={lang} data={resv} withMapBreak={withMap} />
      )}
    </div>
  );
}
